import { useState } from "react";

import { useTranslation } from "react-i18next";

import { ArrowLeftIcon, CheckIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";

import type { ItemRefs } from "components/ideas/useIdeaScrollTracking";
import { getImpactTypeIcon } from "components/lib/Ideas/ImpactStatements/impactIcons";

import { ImpactStatementEditor } from "../../ImpactStatements/ImpactStatementEditor";
import { StageSegment } from "../../ImpactStatements/StageSegments";
import { useImpactStageMap } from "../../ImpactStatements/statementUtils";
import { useImpactStatements } from "../../ImpactStatements/useImpactStatements";

type ImpactStatements =
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"];

type ImpactStatementManagementProps = {
  ideaId: string;
  sectionRefs?: React.RefObject<ItemRefs>;
  scrollRef?: React.RefObject<HTMLDivElement>;
};

const ImpactStatementManagement = ({
  ideaId,
}: ImpactStatementManagementProps) => {
  const { t } = useTranslation();
  const segmentMap = useImpactStageMap();
  const [currentImpactStage, setCurrentImpactStage] = useState<
    NonNullable<ImpactStatements["userEstimate"]>["stage"] | null
  >(null);
  const { statementDefinition, impactStatements, canEditProjectStages } =
    useImpactStatements(ideaId);

  if (currentImpactStage) {
    return (
      <div>
        <Button
          size="small"
          onClick={() => setCurrentImpactStage(null)}
          variant="outline"
        >
          <Icon icon={ArrowLeftIcon} />
          Back to overview
        </Button>

        <Divider />
        <ImpactStatementEditor
          ideaId={ideaId}
          forStage={currentImpactStage}
          restrictStagesTo={[currentImpactStage]}
          editable={
            currentImpactStage !== "userEstimate" && canEditProjectStages
          }
        />

        <Divider hidden />
        <Button onClick={() => setCurrentImpactStage(null)} color="secondary">
          <Icon icon={CheckIcon} />
          Done
        </Button>
      </div>
    );
  }

  return (
    <div>
      {statementDefinition?.showOnIdea ? (
        impactStatements?.userEstimate ? (
          <StageSegment
            ideaId={ideaId}
            stage="userEstimate"
            onView={() => {
              setCurrentImpactStage("userEstimate");
            }}
          />
        ) : (
          <Card fluid>
            <CardContent secondary>
              <div>
                <div>
                  <H3>
                    <Icon icon={segmentMap.userEstimate.icon} />{" "}
                    {segmentMap.userEstimate.name}
                  </H3>
                  {segmentMap.userEstimate.description}
                </div>
                <Message>
                  The {t("generic.idea")} author did not create an estimate.
                </Message>
              </div>
            </CardContent>
          </Card>
        )
      ) : null}
      <StageSegment
        ideaId={ideaId}
        stage="projectEstimate"
        onView={() => {
          setCurrentImpactStage("projectEstimate");
        }}
        onCreated={() => {
          setCurrentImpactStage("projectEstimate");
        }}
      />
      <StageSegment
        ideaId={ideaId}
        stage="finalOutcome"
        onView={() => {
          setCurrentImpactStage("finalOutcome");
        }}
        onCreated={() => {
          setCurrentImpactStage("finalOutcome");
        }}
      />
    </div>
  );
};

export default ImpactStatementManagement;
